import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { payrollAPI } from '../../../services';
import {
  Button,
  Card,
  Select,
  Badge,
  LoadingSpinner,
  ErrorMessage,
  SuccessMessage,
} from '../../UI';
import {
  Download,
  Upload,
  Users,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';

// EPFO business rules
const EPS_EDLI_CEILING = 15000;
const EMPLOYEE_PF_RATE = 0.12;
const EMPLOYER_EPS_RATE = 0.0833;
const MONTH_DAYS = 30;

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const UAN_REGEX = /^\d{12}$/;

function computeContributionsFromGross(grossWages, ncpDays = 0) {
  const epfWages = Math.round(Number(grossWages) || 0);
  const epsEdliWages = Math.min(epfWages, EPS_EDLI_CEILING);
  const employeePf = Math.round(epfWages * EMPLOYEE_PF_RATE);
  const employerEps = Math.round(epsEdliWages * EMPLOYER_EPS_RATE);
  const employerPf = employeePf - employerEps;
  return {
    epfWages,
    epsWages: epsEdliWages,
    edliWages: epsEdliWages,
    employeePf,
    employerEps,
    employerPf,
    ncpDays: Number(ncpDays) || 0,
    refund: 0,
  };
}

function validateRow(row) {
  const errors = [];
  const warnings = [];

  if (!UAN_REGEX.test(String(row.uan || '').trim())) {
    errors.push('UAN must be exactly 12 digits');
  }
  if (!row.name || !String(row.name).trim()) {
    errors.push('Name is required');
  }
  if (!row.epfWages || row.epfWages <= 0) {
    errors.push('Gross wages must be greater than 0');
  }
  if (row.ncpDays < 0 || row.ncpDays > MONTH_DAYS) {
    errors.push(`NCP days must be between 0 and ${MONTH_DAYS}`);
  }
  if (row.employeePf !== row.employerEps + row.employerPf) {
    errors.push('Employee PF does not match Employer EPS + Employer PF');
  }
  if (row.sourcePf != null && Math.abs(row.sourcePf - row.employeePf) > 1) {
    warnings.push(`Computed EE PF (₹${row.employeePf}) differs from payroll deduction (₹${row.sourcePf})`);
  }

  return { errors, warnings };
}

function buildEcrLine(row) {
  return [
    row.uan,
    row.name,
    row.epfWages,
    row.epfWages,
    row.epsWages,
    row.edliWages,
    row.employeePf,
    row.employerEps,
    row.employerPf,
    row.ncpDays,
    row.refund,
  ].join('#~#');
}

async function parseExcelFile(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });

  return json.map((entry) => {
    const keyMap = Object.keys(entry).reduce((acc, key) => {
      acc[key.trim().toLowerCase()] = entry[key];
      return acc;
    }, {});

    return {
      uan: String(keyMap.uan || '').trim(),
      name: String(keyMap.name || '').trim(),
      grossWages: Number(keyMap['gross salary'] ?? keyMap.gross ?? 0),
    };
  });
}

const PFEcrPage = () => {
  const now = new Date();
  const [mode, setMode] = useState('digihr');
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [year, setYear] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [overrides, setOverrides] = useState({});

  const buildRow = (base, ncpDays, sourcePf) => {
    const contributions = computeContributionsFromGross(base.grossWages, ncpDays);
    const row = {
      uan: base.uan,
      name: base.name,
      sourcePf: sourcePf ?? null,
      ...contributions,
    };
    const { errors, warnings } = validateRow(row);
    return { ...row, errors, warnings };
  };

  const loadDigiHrEmployees = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setRows([]);
    try {
      const res = await payrollAPI.getPfEcrEmployees(month, year);
      const employees = res.data || [];
      if (employees.length === 0) {
        setError('No PF-eligible employees with generated payslips found for this period.');
      }
      setRows(employees.map((emp) => buildRow(
        { uan: emp.uan, name: emp.name, grossWages: emp.grossWages },
        emp.ncpDays,
        emp.employeePf
      )));
    } catch (err) {
      setError(err.message || 'Failed to load employees for this period');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setError('');
    setSuccess('');
    setRows([]);
    try {
      const parsed = await parseExcelFile(file);
      if (parsed.length === 0) {
        setError('No rows found in the uploaded file. Expected columns: UAN, Name, Gross Salary');
      }
      setRows(parsed.map((entry) => buildRow(entry, 0, null)));
    } catch (err) {
      setError('Failed to parse the Excel file. Please check the format.');
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const updateNcpDays = (index, value) => {
    setRows((prev) => prev.map((row, i) => {
      if (i !== index) return row;
      return buildRow(
        { uan: row.uan, name: row.name, grossWages: row.epfWages },
        value,
        row.sourcePf
      );
    }));
  };

  const toggleOverride = (index) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (next[index]) {
        delete next[index];
      } else {
        // eslint-disable-next-line no-alert
        const confirmed = window.confirm('Override validation errors for this row and allow it in the download?');
        if (!confirmed) return prev;
        next[index] = true;
      }
      return next;
    });
  };

  const blockingRows = rows.filter((row, i) => row.errors.length > 0 && !overrides[i]);
  const canDownload = rows.length > 0 && blockingRows.length === 0;

  const handleDownload = () => {
    if (!canDownload) return;
    const lines = rows.map(buildEcrLine).join('\n');
    const blob = new Blob([lines], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ECR_upload.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSuccess(`ECR file generated for ${rows.length} employee(s).`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">PF ECR Generation</h1>
          <p className="text-secondary-600">Generate the EPFO-compatible ECR upload file</p>
        </div>
        <div className="flex gap-2">
          <Button variant={mode === 'digihr' ? 'primary' : 'outline'} onClick={() => { setMode('digihr'); setRows([]); setError(''); setSuccess(''); }}>
            <Users className="w-4 h-4 mr-2" /> DigiHR Mode
          </Button>
          <Button variant={mode === 'excel' ? 'primary' : 'outline'} onClick={() => { setMode('excel'); setRows([]); setError(''); setSuccess(''); }}>
            <Upload className="w-4 h-4 mr-2" /> Excel Upload Mode
          </Button>
        </div>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      <Card title="Pay Period">
        <div className="flex flex-wrap items-end gap-4">
          <Select label="Month" value={month} onChange={(e) => setMonth(Number(e.target.value))} containerClassName="w-40">
            {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </Select>
          <Select label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))} containerClassName="w-32">
            {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </Select>

          {mode === 'digihr' ? (
            <Button onClick={loadDigiHrEmployees} loading={loading}>
              <Users className="w-4 h-4 mr-2" /> Load Employees
            </Button>
          ) : (
            <label className="btn btn-outline cursor-pointer">
              <Upload className="w-4 h-4 mr-2" /> Upload Excel
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileUpload} />
            </label>
          )}
        </div>
      </Card>

      {loading && <div className="flex justify-center py-8"><LoadingSpinner /></div>}

      {!loading && rows.length > 0 && (
        <Card title={`Employees (${rows.length})`}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-secondary-500 border-b">
                  <th className="py-2 pr-4">UAN</th>
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">EPF Wages</th>
                  <th className="py-2 pr-4">EPS Wages</th>
                  <th className="py-2 pr-4">EE PF</th>
                  <th className="py-2 pr-4">ER EPS</th>
                  <th className="py-2 pr-4">ER PF</th>
                  <th className="py-2 pr-4">NCP Days</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={`${row.uan}-${i}`} className="border-b last:border-0">
                    <td className="py-2 pr-4">{row.uan}</td>
                    <td className="py-2 pr-4">{row.name}</td>
                    <td className="py-2 pr-4">₹{row.epfWages}</td>
                    <td className="py-2 pr-4">₹{row.epsWages}</td>
                    <td className="py-2 pr-4">₹{row.employeePf}</td>
                    <td className="py-2 pr-4">₹{row.employerEps}</td>
                    <td className="py-2 pr-4">₹{row.employerPf}</td>
                    <td className="py-2 pr-4">
                      <input
                        type="number"
                        min="0"
                        max={MONTH_DAYS}
                        value={row.ncpDays}
                        onChange={(e) => updateNcpDays(i, e.target.value)}
                        className="form-input w-20"
                      />
                    </td>
                    <td className="py-2 pr-4">
                      {row.errors.length > 0 ? (
                        <div className="flex items-center gap-2">
                          <Badge variant="danger"><AlertTriangle className="w-3 h-3 inline mr-1" />{row.errors.length} error(s)</Badge>
                          <button type="button" className="text-xs underline text-secondary-500" onClick={() => toggleOverride(i)}>
                            {overrides[i] ? 'Overridden' : 'Override'}
                          </button>
                        </div>
                      ) : row.warnings.length > 0 ? (
                        <Badge variant="warning">{row.warnings.length} warning(s)</Badge>
                      ) : (
                        <Badge variant="success"><CheckCircle2 className="w-3 h-3 inline mr-1" />Valid</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex justify-end">
            <Button onClick={handleDownload} disabled={!canDownload}>
              <Download className="w-4 h-4 mr-2" /> Download ECR_upload.txt
            </Button>
          </div>
          {!canDownload && rows.length > 0 && (
            <p className="text-sm text-danger-600 mt-2 text-right">
              Resolve or override {blockingRows.length} row(s) with errors before downloading.
            </p>
          )}
        </Card>
      )}
    </div>
  );
};

export default PFEcrPage;
